import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2 } from 'lucide-react';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';
import TypewriterText from '../../components/TypewriterText';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';

const OnboardingStep2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { preferences, updatePreferences } = useUserPreferences();
  const [currency, setCurrency] = useState(preferences.currency);
  const [thousandSeparator, setThousandSeparator] = useState(preferences.thousandSeparator);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const progress = (2 / 14) * 100;

  const currencies = [
    { code: 'IDR', label: 'Indonesian Rupiah (Rp)' },
    { code: 'USD', label: 'US Dollar ($)' },
    { code: 'EUR', label: 'Euro (€)' },
    { code: 'GBP', label: 'British Pound (£)' },
    { code: 'JPY', label: 'Japanese Yen (¥)' },
    { code: 'SGD', label: 'Singapore Dollar (S$)' },
    { code: 'MYR', label: 'Malaysian Ringgit (RM)' },
    { code: 'INR', label: 'Indian Rupee (₹)' },
  ];

  const separators = [
    { value: '.', label: 'Titik (1.234.567)' },
    { value: ',', label: 'Koma (1,234,567)' },
  ];

  const handleNext = async () => {
    setIsSubmitting(true);
    try {
      await updatePreferences(currency, thousandSeparator, preferences.language);
      navigate('/onboarding/step-3');
    } catch (error) {
      console.error('Failed to update preferences:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingLayout progress={progress}>
      <div className="bg-white border-4 border-[#077f5d] p-8" style={{ boxShadow: '6px 6px 0 #077f5d' }}>
        <div className="text-center mb-8">
          <h1
            className="text-2xl text-center text-[#077f5d] mb-4 uppercase"
            style={{ fontFamily: "'Press Start 2P'", lineHeight: '1.3' }}
          >
            {t('onboarding.step2.title')}
          </h1>
          <div className="space-y-3 mb-6">
            <TypewriterText
              texts={[
                t('onboarding.step2.subtitle')
              ]}
              speed={35}
              delayBetween={500}
              showCursor={true}
              className="text-[#1a1a1a] font-mono text-center"
              onFinish={() => console.log('Typing done')}
              startOnView={true}
            />
          </div>
        </div>

        <div className="space-y-6 mb-8">
          {/* Combined Currency & Separator Selection */}
          <div>
            <label className="block text-[#077f5d] mb-3 uppercase font-bold font-mono text-xs">
              {t('onboarding.step2.label_currency_format')}
            </label>

            {/* Combined Display */}
            <div className="flex gap-2">
              {/* Currency Symbol Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="appearance-none px-6 py-4 pr-10 border-2 border-[#077f5d] bg-white font-mono font-bold text-lg hover:bg-[#dffbd0] cursor-pointer"
                  style={{
                    boxShadow: '2px 2px 0 #077f5d',
                    transition: 'none'
                  }}
                  disabled={isSubmitting}
                >
                  {currencies.map(c => (
                    <option key={c.code} value={c.code}>
                      {(() => {
                        const symbols: Record<string, string> = {
                          'IDR': 'Rp',
                          'USD': '$',
                          'EUR': '€',
                          'GBP': '£',
                          'JPY': '¥',
                          'SGD': 'S$',
                          'MYR': 'RM',
                          'INR': '₹'
                        };
                        return symbols[c.code] || c.code;
                      })()}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#077f5d] pointer-events-none" />
              </div>

              {/* Formatted Amount Dropdown */}
              <div className="relative flex-1">
                <select
                  value={thousandSeparator}
                  onChange={(e) => setThousandSeparator(e.target.value)}
                  className="appearance-none w-full px-6 py-4 pr-10 border-2 border-[#077f5d] bg-white font-mono font-bold text-lg hover:bg-[#dffbd0] text-right cursor-pointer"
                  style={{
                    boxShadow: '2px 2px 0 #077f5d',
                    transition: 'none'
                  }}
                  disabled={isSubmitting}
                >
                  {separators.map(s => (
                    <option key={s.value} value={s.value}>
                      {(() => {
                        const n = 12345;
                        const locale = s.value === '.' ? 'id-ID' : 'en-US';
                        return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
                      })()}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#077f5d] pointer-events-none" />
              </div>
            </div>
          </div>


        </div>

        <button
          type="submit"
          onClick={handleNext}
          disabled={isSubmitting}
          className="w-full bg-[#3cbe8c] text-white px-8 py-4 border-2 border-[#077f5d] font-bold uppercase flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            fontFamily: "'Press Start 2P', monospace",
            fontSize: '12px',
            boxShadow: '6px 6px 0 #077f5d'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }
          }}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('onboarding.step2.saving')}
            </>
          ) : (
            t('onboarding.step2.next')
          )}
        </button>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingStep2;
